// src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import logger from './utils/logger.js';

// Import existing components
import Navbar from './components/layout/Navbar.js';
import Footer from './components/layout/Footer.js';
import LoadingSpinner from './components/common/LoadingSpinner.js';
import LazyLoadingSpinner from './components/common/LazyLoadingSpinner.js';
import ErrorBoundary from './components/common/ErrorBoundary.js';
import RouteErrorBoundary from './components/common/RouteErrorBoundary.js';
import PerformanceDashboard from './components/PerformanceDashboard.js';
import SecurityDashboard from './components/SecurityDashboard.js';
import { SecurityProvider } from './contexts/SecurityContext.js';
import Home from './pages/Home.js';
import Login from './pages/auth/Login.js';
import Register from './pages/auth/Register.js';
import GoogleCallback from './pages/auth/GoogleCallback.js';
import Profile from './pages/account/ProfilePage.js';
import Settings from './pages/Settings.js';
import NotFound from './pages/NotFound.js';

// Import subscription-related components
import PaymentSuccessPage from './pages/subscription/PaymentSuccessPage.js';
import PaymentCancelPage from './pages/subscription/PaymentCancelPage.js';
import BasicSuccessPage from './pages/subscription/BasicSuccessPage.js';
import SubscriptionUpgradePage from './pages/subscription/SubscriptionUpgradePage.js';
import PricingPlans from './components/subscription/PricingPlans.js';
import SubscriptionManagement from './components/subscription/SubscriptionManagement.js';

// Import safety-related pages
import SafetyGuidelines from './pages/safety/Guidelines.js';
import SafetyScoreAnalysis from './pages/safety/SafetyScoreAnalysis.js';
import FatigueTracking from './pages/safety/FatigueTracking.js';
import EmergencyProcedures from './pages/EmergencyProcedures.js';
import SafetyFAQ from './pages/SafetyFAQ.js';

// Import auth context
import { useAuth, AuthProvider } from './contexts/AuthContext.js';
// Import subscription context
import { useSubscription, SubscriptionProvider } from './contexts/SubscriptionContext.js';
// Import safety context
import { SafetyProvider } from './contexts/SafetyContext.js';

import FAQ from './pages/FAQ.js';
import Help from './pages/Help.js';
import Contact from './pages/Contact.js';
import TermsAndConditions from './pages/TermsAndConditions.js';
import PrivacyPolicy from './pages/PrivacyPolicy.js';

// Import the new AITrainingDashboard component
import AITrainingDashboard from './components/training/AITrainingDashboard.js';

// Import HealthHub component
import HealthHub from './pages/health/HealthHub.js';
// Import HealthProvider
import { HealthProvider } from './contexts/HealthContext.js';
import EducationalContent from './pages/education/EducationalContent.js';
import JointHealthArticle from './pages/education/JointHealthArticle.js';
import SeniorNutritionArticle from './pages/education/SeniorNutritionArticle.js';
import SleepRecoveryArticle from './pages/education/SleepRecoveryArticle.js';
import CloudinaryTest from './components/examples/CloudinaryTest.js';
import VideoUploadExample from './components/examples/VideoUploadExample.js';
import SimpleTest from './components/examples/SimpleTest.js';
import IsolatedTest from './components/examples/IsolatedTest.js';
import VideoListTest from './components/examples/VideoListTest.js';
import MonthlyPackages from './pages/MonthlyPackages.js';
import MonthlyPackageDetail from './pages/MonthlyPackageDetail.js';
import WOWContent from './pages/WOWContent.js';
import CourseLanding from './pages/CourseLanding.js';
import SalesPage from './pages/SalesPage.js';
import { AppProviders } from './providers/AppProviders.js';
import ProtectedRoute from './components/ProtectedRoute.js';
import PublicRoute from './components/PublicRoute.js';
import AdminRoute from './components/AdminRoute.js';

// Lazy load heavy components for better performance
const Dashboard = lazy(() => import('./pages/Dashboard.js'));
const BasicContent = lazy(() => import('./pages/subscription/BasicContent.js'));
const PremiumContent = lazy(() => import('./pages/subscription/PremiumContent.js'));
const EliteContent = lazy(() => import('./pages/subscription/EliteContent.js'));
const SpecializedPrograms = lazy(() => import('./pages/elite/SpecializedPrograms.js'));
const FamilyPlan = lazy(() => import('./pages/elite/FamilyPlan.js'));
const Analytics = lazy(() => import('./pages/elite/Analytics.js'));
const PremiumContentLibrary = lazy(() => import('./pages/elite/PremiumContent.js'));
const SafetyFeaturesPage = lazy(() => import('./pages/SafetyFeaturesPage.js'));
const WorkoutsPage = lazy(() => import('./pages/WorkoutsPage.js'));
const WorkoutDetail = lazy(() => import('./pages/WorkoutDetail.js'));
const EquipmentTagging = lazy(() => import('./pages/admin/EquipmentTagging.js'));
const CommentModeration = lazy(() => import('./pages/admin/CommentModeration.js'));

// Main App component with debugging
function App() {
  const location = useLocation();
  
  // Check if we're on a test route
  const isTestRoute = ['/isolated-test', '/test-cloudinary', '/test-video-upload', '/video-test', '/simple-test', '/video-list-test'].includes(location.pathname);
  
  // Add effect for scrolling to top on route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Register service worker for image caching
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          logger.debug('Service Worker registered successfully:', registration);
        })
        .catch((error) => {
          logger.error('Service Worker registration failed:', error);
        });
    }
  }, []);
  
  // Add debug logging for App component
  useEffect(() => {
    logger.debug('App mounted, current path:', location.pathname);
    logger.debug('Is test route:', isTestRoute);
    
    // Check if we're on the root path and log it
    if (location.pathname === '/') {
      logger.debug('App is on root path (/)');
    }
    
    // Log all route attempts
    logger.debug('Attempting to route to:', location.pathname);
  }, [location.pathname, isTestRoute]);

  // If we're on a test route, render only the test component
  if (isTestRoute) {
    logger.debug('Rendering test route:', location.pathname);
    switch (location.pathname) {
      case '/isolated-test':
        return <IsolatedTest />;
      case '/test-cloudinary':
        return <CloudinaryTest />;
      case '/test-video-upload':
        return <VideoUploadExample />;
      case '/video-test':
        return <VideoUploadExample />;
      case '/simple-test':
        return <SimpleTest />;
      case '/video-list-test':
        return (
          <AppProviders>
            <VideoListTest />
          </AppProviders>
        );
      default:
        return <IsolatedTest />;
    }
  }

  // Main app routes
  return (
    <AppProviders>
      <SecurityProvider>
        <HealthProvider>
          <div className="App">
            <Navbar />
            <main className="main-content">
              <ErrorBoundary>
                <Suspense fallback={<LazyLoadingSpinner message="Loading page..." />}>
                  <Routes>
              {/* Home route */}
              <Route path="/" element={<Home />} />
              
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/google/callback" element={<GoogleCallback />} />
              <Route path="/course" element={<CourseLanding />} />
              <Route path="/sales" element={<SalesPage />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/subscription/plans" element={<PricingPlans />} />
              <Route path="/subscription/success" element={<PaymentSuccessPage />} />
              <Route path="/subscription/cancel" element={<PaymentCancelPage />} />
              <Route path="/subscription/basic-success" element={<BasicSuccessPage />} />
              
              {/* Redirect old pricing page to new subscription plans page */}
              <Route path="/pricing" element={<Navigate to="/subscription/plans" replace />} />
              
              {/* Protected routes with explicit checks */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Dashboard">
                      <Suspense fallback={<LazyLoadingSpinner message="Loading dashboard..." />}>
                        <Dashboard />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/workouts" 
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Workouts">
                      <Suspense fallback={<LazyLoadingSpinner message="Loading workouts..." />}>
                        <WorkoutsPage />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/workouts/:id" 
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Workout Details">
                      <Suspense fallback={<LazyLoadingSpinner message="Loading workout details..." />}>
                        <WorkoutDetail />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              
              {/* Monthly Packages Routes */}
              <Route 
                path="/monthly-packages" 
                element={
                  <ProtectedRoute>
                    <MonthlyPackages />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/monthly-packages/:id" 
                element={
                  <ProtectedRoute>
                    <MonthlyPackageDetail />
                  </ProtectedRoute>
                }
              />
              
              {/* WOW Content Route */}
              <Route 
                path="/wow" 
                element={
                  <ProtectedRoute>
                    <WOWContent />
                  </ProtectedRoute>
                }
              />
              
              <Route 
                path="/subscription/manage" 
                element={
                  <ProtectedRoute>
                    <SubscriptionManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/subscription/upgrade" 
                element={
                  <ProtectedRoute>
                    <SubscriptionUpgradePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } 
              />
              
              {/* Settings Page */}
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } 
              />
              
              {/* Tier-specific content routes */}
              <Route 
                path="/content/basic" 
                element={
                  <ProtectedRoute>
                    <RouteErrorBoundary routeName="Basic Content">
                      <Suspense fallback={<LazyLoadingSpinner message="Loading basic content..." />}>
                        <BasicContent />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/content/premium" 
                element={
                  <ProtectedRoute requiredTier="premium">
                    <RouteErrorBoundary routeName="Premium Content">
                      <Suspense fallback={<LazyLoadingSpinner message="Loading premium content..." />}>
                        <PremiumContent />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/content/elite" 
                element={
                  <ProtectedRoute requiredTier="elite">
                    <RouteErrorBoundary routeName="Elite Content">
                      <Suspense fallback={<LazyLoadingSpinner message="Loading elite content..." />}>
                        <EliteContent />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              
              {/* Safety Features Page */}
              <Route 
                path="/safety" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyLoadingSpinner message="Loading safety features..." />}>
                      <SafetyFeaturesPage />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              
              
              {/* Safety Guidelines Page (public) */}
              <Route path="/safety/guidelines" element={<SafetyGuidelines />} />
              
              {/* Safety Score Analysis Page (public for testing) */}
              <Route path="/safety/recommendations" element={<SafetyScoreAnalysis />} />
              
              {/* Fatigue Tracking Page */}
              <Route path="/safety/fatigue" element={<FatigueTracking />} />
              
              {/* Emergency Procedures Page (public) */}
              <Route path="/safety/emergency" element={<EmergencyProcedures />} />
              
              {/* Safety FAQ Page (public) */}
              <Route path="/safety/faq" element={<SafetyFAQ />} />
              
              {/* FAQ Page (public) */}
              <Route path="/faq" element={<FAQ />} />
              
              {/* Help Page (public) */}
              <Route path="/help" element={<Help />} />
              
              {/* Contact Page (public) */}
              <Route path="/contact" element={<Contact />} />
              
              {/* AI Training Page */}
              <Route 
                path="/elite/ai-training" 
                element={
                  <ProtectedRoute requiredTier="elite">
                    <AITrainingDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Health Hub Page */}
              <Route 
                path="/elite/health-hub" 
                element={
                  <ProtectedRoute requiredTier="elite">
                    <HealthHub />
                  </ProtectedRoute>
                } 
              />
              
              {/* Specialized Programs Page */}
              <Route 
                path="/elite/specialized-programs" 
                element={
                  <ProtectedRoute requiredTier="elite">
                    <RouteErrorBoundary routeName="Specialized Programs">
                      <Suspense fallback={<LazyLoadingSpinner message="Loading specialized programs..." />}>
                        <SpecializedPrograms />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              
              {/* Family Plan Page */}
              <Route 
                path="/elite/family-plan" 
                element={
                  <ProtectedRoute requiredTier="elite">
                    <Suspense fallback={<LazyLoadingSpinner message="Loading family plan..." />}>
                      <FamilyPlan />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              
              {/* Analytics Page */}
              <Route 
                path="/elite/analytics" 
                element={
                  <ProtectedRoute requiredTier="elite">
                    <Suspense fallback={<LazyLoadingSpinner message="Loading analytics..." />}>
                      <Analytics />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              
              {/* Premium Content Page */}
              <Route 
                path="/elite/premium-content" 
                element={
                  <ProtectedRoute requiredTier="elite">
                    <Suspense fallback={<LazyLoadingSpinner message="Loading premium content library..." />}>
                      <PremiumContentLibrary />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              
              
              {/* Educational Content Routes */}
              <Route 
                path="/education" 
                element={
                  <ProtectedRoute requiredTier="premium">
                    <EducationalContent />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/education/joint-health" 
                element={
                  <ProtectedRoute requiredTier="premium">
                    <JointHealthArticle />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/education/senior-nutrition" 
                element={
                  <ProtectedRoute requiredTier="premium">
                    <SeniorNutritionArticle />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/education/sleep-recovery" 
                element={
                  <ProtectedRoute requiredTier="premium">
                    <SleepRecoveryArticle />
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin Routes */}
              <Route 
                path="/admin/equipment" 
                element={
                  <AdminRoute>
                    <Suspense fallback={<LazyLoadingSpinner message="Loading admin panel..." />}>
                      <EquipmentTagging />
                    </Suspense>
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/comments" 
                element={
                  <AdminRoute>
                    <Suspense fallback={<LazyLoadingSpinner message="Loading comment moderation..." />}>
                      <CommentModeration />
                    </Suspense>
                  </AdminRoute>
                } 
              />
              
              {/* Test Routes moved to top for priority */}
              
              
              {/* Catch-all route for 404 - MUST be last */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
            </ErrorBoundary>
            </main>
            <Footer />
            <PerformanceDashboard />
            <SecurityDashboard />
          </div>
        </HealthProvider>
      </SecurityProvider>
    </AppProviders>
  );
}

export default App;
